import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export default function HeroSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = () => {
    const query = searchQuery.trim();
    if (query) {
      navigate(`/hospitals?search=${encodeURIComponent(query)}`);
    } else {
      navigate('/hospitals');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/d2a19b1213fcb347dfbfc6c94526a126.jpg"
          alt="Medical Tourism Background"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
          {t('hero_title')}
        </h1>
        <p className="text-xl md:text-2xl text-white/90 mb-4 font-medium">
          {t('hero_subtitle')}
        </p>
        <p className="text-base md:text-lg text-white/80 mb-8 max-w-3xl mx-auto leading-relaxed">
          {t('hero_description')}
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button
            onClick={() => navigate('/consultation')}
            className="px-8 py-3 bg-teal-600 text-white text-base font-medium rounded-md hover:bg-teal-700 transition-colors shadow-lg whitespace-nowrap cursor-pointer"
          >
            {t('hero_cta_primary')}
          </button>
          <button
            onClick={() => navigate('/hospitals')}
            className="px-8 py-3 bg-white text-teal-600 text-base font-medium rounded-md hover:bg-gray-50 transition-colors shadow-lg whitespace-nowrap cursor-pointer"
          >
            {t('hero_cta_secondary')}
          </button>
        </div>

        {/* Search Bar */}
        <div className="mt-12 max-w-3xl mx-auto">
          <div className="bg-white rounded-lg shadow-xl p-2 flex flex-col sm:flex-row gap-2">
            <div className="flex-1 flex items-center px-4">
              <i className="ri-search-line text-xl text-gray-400 mr-3"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('search_placeholder')}
                className="flex-1 py-3 text-sm focus:outline-none text-gray-800"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="ml-2 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-base"></i>
                </button>
              )}
            </div>
            <button
              onClick={handleSearch}
              className="px-6 py-3 bg-teal-600 text-white text-sm font-medium rounded-md hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('search_button')}
            </button>
          </div>
          {/* Search hint tags */}
          <div className="mt-3 flex flex-wrap gap-2 justify-center">
            {[
              { uz: 'Tish implant', ru: 'Зубной имплант' },
              { uz: 'Ko\'z LASIK', ru: 'Лазерная коррекция' },
              { uz: 'Plastik jarrohlik', ru: 'Пластическая хирургия' },
              { uz: 'Ildiz hujayra', ru: 'Стволовые клетки' },
            ].map((tag, i) => (
              <button
                key={i}
                onClick={() => {
                  setSearchQuery(tag.uz);
                  navigate(`/hospitals?search=${encodeURIComponent(tag.uz)}`);
                }}
                className="px-3 py-1 bg-white/20 hover:bg-white/30 text-white text-xs rounded-full border border-white/30 transition-colors cursor-pointer whitespace-nowrap"
              >
                {tag.uz} / {tag.ru}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <i className="ri-arrow-down-line text-3xl text-white"></i>
      </div>
    </section>
  );
}